import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView,
  TextInput, Dimensions, Animated, Platform, StatusBar,
  BackHandler, FlatList, Alert,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── AI SERVICE ───────────────────────────────────────────────────────────────
let aiService = null;
try { aiService = require('../../../services/aiService'); } catch {}


const { width: W } = Dimensions.get('window');

const BRAND = {
  blue: '#0e4e96', canary: '#fdb913', mint: '#19abb5',
  bg: '#f8fafc', surface: '#ffffff', text: '#0f172a', sub: '#475569',
  line: '#e2e8f0', success: '#10b981', warning: '#f59e0b',
  danger: '#ef4444', info: '#3b82f6', purple: '#8b5cf6', pink: '#ec4899',
};

// ─── GLOBAL SEARCH INDEX ──────────────────────────────────────────────────────
const SEARCH_INDEX = [
  // CRM
  { id: 'C001', type: 'deal', module: 'CRM', title: 'Enterprise Software License', sub: 'Acme Corp · John Smith', value: '£45,000', status: 'negotiation', icon: 'briefcase', color: '#f97316', tags: ['enterprise', 'active'] },
  { id: 'C002', type: 'deal', module: 'CRM', title: 'Consulting Package', sub: 'Beta Ltd · Jane Doe', value: '£28,000', status: 'proposal', icon: 'briefcase', color: '#f97316', tags: ['consulting'] },
  { id: 'C003', type: 'contact', module: 'CRM', title: 'John Smith', sub: 'CEO · Acme Corporation', value: 'Score: 92', status: 'active', icon: 'account', color: '#f97316', tags: ['enterprise', 'key-account'] },
  { id: 'C004', type: 'contact', module: 'CRM', title: 'Jane Doe', sub: 'CTO · Beta Industries', value: 'Score: 74', status: 'active', icon: 'account', color: '#f97316', tags: ['mid-market'] },
  { id: 'C005', type: 'deal', module: 'CRM', title: 'Platform Migration', sub: 'Nexus Health · Dr. Patel', value: '£120,000', status: 'lead', icon: 'briefcase', color: '#f97316', tags: ['healthcare', 'high-value'] },
  // Finance
  { id: 'F001', type: 'invoice', module: 'Finance', title: 'INV-2024-089 – Acme Corp', sub: 'Invoice · £22,000', value: '+£22,000', status: 'paid', icon: 'file-document', color: BRAND.success, tags: ['invoice', 'paid'] },
  { id: 'F002', type: 'invoice', module: 'Finance', title: 'INV-2024-012 – XYZ Corp', sub: 'Invoice · £8,500 OVERDUE', value: '£8,500', status: 'overdue', icon: 'file-document', color: BRAND.danger, tags: ['invoice', 'overdue'] },
  { id: 'F003', type: 'transaction', module: 'Finance', title: 'Office Rent – January', sub: 'Expense · Operations', value: '-£8,500', status: 'completed', icon: 'home', color: BRAND.success, tags: ['expense', 'recurring'] },
  { id: 'F004', type: 'transaction', module: 'Finance', title: 'Software Licences Q1', sub: 'Expense · Technology', value: '-£4,200', status: 'completed', icon: 'laptop', color: BRAND.success, tags: ['expense', 'technology'] },
  { id: 'F005', type: 'invoice', module: 'Finance', title: 'INV-2025-001 – Delta Tech', sub: 'Invoice · £45,000 Draft', value: '£45,000', status: 'draft', icon: 'file-document', color: BRAND.info, tags: ['invoice', 'draft'] },
  // HR
  { id: 'H001', type: 'employee', module: 'HR', title: 'Sarah Johnson', sub: 'Senior Developer · Engineering', value: '£72,000/yr', status: 'active', icon: 'account', color: BRAND.purple, tags: ['developer', 'engineering'] },
  { id: 'H002', type: 'employee', module: 'HR', title: 'Mike Chen', sub: 'Marketing Manager · Marketing', value: '£58,000/yr', status: 'active', icon: 'account', color: BRAND.purple, tags: ['marketing'] },
  { id: 'H003', type: 'employee', module: 'HR', title: 'Lucy Brown', sub: 'Sales Executive · Sales', value: '£44,000/yr', status: 'leave', icon: 'account', color: BRAND.purple, tags: ['sales', 'leave'] },
  { id: 'H004', type: 'payroll', module: 'HR', title: 'January 2025 Payroll', sub: '245 employees · £924,000', value: '£924,000', status: 'pending', icon: 'currency-gbp', color: BRAND.purple, tags: ['payroll', 'pending'] },
  // Inventory
  { id: 'I001', type: 'product', module: 'Inventory', title: 'Laptop Pro 15"', sub: 'SKU: LP-15-001 · Electronics', value: 'Qty: 45', status: 'in-stock', icon: 'laptop', color: BRAND.mint, tags: ['electronics', 'in-stock'] },
  { id: 'I002', type: 'product', module: 'Inventory', title: 'USB-C Cable 2m', sub: 'SKU: UC-001 · Accessories', value: 'Qty: 12 ⚠', status: 'low-stock', icon: 'cable-data', color: BRAND.danger, tags: ['accessories', 'low-stock', 'urgent'] },
  { id: 'I003', type: 'product', module: 'Inventory', title: 'Printer Ink Black', sub: 'SKU: PI-BK1 · Consumables', value: 'Qty: 8 ⚠', status: 'low-stock', icon: 'printer', color: BRAND.danger, tags: ['consumables', 'low-stock', 'urgent'] },
  { id: 'I004', type: 'product', module: 'Inventory', title: '4K Monitor 27"', sub: 'SKU: MN-270 · Electronics', value: 'Qty: 36', status: 'in-stock', icon: 'monitor', color: BRAND.mint, tags: ['electronics'] },
  // Projects
  { id: 'P001', type: 'project', module: 'Projects', title: 'Alpha Initiative', sub: 'Sarah Johnson · 75% complete', value: '£125,000', status: 'active', icon: 'folder', color: '#6366f1', tags: ['active', 'high-priority'] },
  { id: 'P002', type: 'project', module: 'Projects', title: 'Mobile App v3', sub: 'Anna Lee · 60% complete', value: '£200,000', status: 'active', icon: 'folder', color: '#6366f1', tags: ['active', 'high-value'] },
  { id: 'P003', type: 'task', module: 'Projects', title: 'Database Migration', sub: 'Alpha Initiative · John Doe', value: 'Due 1 Feb', status: 'in-progress', icon: 'check-circle', color: '#6366f1', tags: ['task', 'backend'] },
  { id: 'P004', type: 'project', module: 'Projects', title: 'ERP Migration', sub: 'Robert Davis · 30% complete', value: '£350,000', status: 'active', icon: 'folder', color: '#6366f1', tags: ['active', 'high-value', 'erp'] },
  // Procurement
  { id: 'PR001', type: 'purchase_order', module: 'Procurement', title: 'Server Hardware Upgrade', sub: 'Dell Technologies · Approved', value: '£34,000', status: 'approved', icon: 'server', color: BRAND.warning, tags: ['hardware', 'IT'] },
  { id: 'PR002', type: 'supplier', module: 'Procurement', title: 'Dell Technologies', sub: '3 active orders · IT Hardware', value: '£34,000', status: 'active', icon: 'domain', color: BRAND.warning, tags: ['supplier', 'IT'] },
  // Production
  { id: 'WO001', type: 'work_order', module: 'Production', title: 'Batch B – Assembly Y', sub: 'Line 2 · 93% complete', value: '280/300 units', status: 'in-progress', icon: 'factory', color: BRAND.danger, tags: ['production', 'active'] },
];

const RECENT_SEARCHES = ['John Smith', 'overdue invoices', 'Alpha Initiative', 'low stock', 'January payroll'];

const AI_SUGGESTIONS = [
  'Show overdue invoices',
  'Find low stock items',
  'Active deals over £50k',
  'Projects due this month',
  'Employees on leave',
  'Pending payroll approvals',
  'High priority tasks',
  'Suppliers with open orders',
];

const TYPE_ICONS = {
  deal: { icon: 'briefcase', color: '#f97316', label: 'Deal' },
  contact: { icon: 'account', color: '#f97316', label: 'Contact' },
  invoice: { icon: 'file-document', color: BRAND.success, label: 'Invoice' },
  transaction: { icon: 'swap-horizontal', color: BRAND.success, label: 'Transaction' },
  employee: { icon: 'account', color: BRAND.purple, label: 'Employee' },
  payroll: { icon: 'currency-gbp', color: BRAND.purple, label: 'Payroll' },
  product: { icon: 'package', color: BRAND.mint, label: 'Product' },
  project: { icon: 'folder', color: '#6366f1', label: 'Project' },
  task: { icon: 'check-circle', color: '#6366f1', label: 'Task' },
  purchase_order: { icon: 'file-sign', color: BRAND.warning, label: 'PO' },
  supplier: { icon: 'domain', color: BRAND.warning, label: 'Supplier' },
  work_order: { icon: 'factory', color: BRAND.danger, label: 'Work Order' },
};

const MODULE_FILTERS = [
  { id: 'all', label: 'All', icon: 'apps', color: BRAND.blue },
  { id: 'CRM', label: 'CRM', icon: 'handshake', color: '#f97316' },
  { id: 'Finance', label: 'Finance', icon: 'cash-multiple', color: BRAND.success },
  { id: 'HR', label: 'HR', icon: 'account-group', color: BRAND.purple },
  { id: 'Inventory', label: 'Stock', icon: 'warehouse', color: BRAND.mint },
  { id: 'Projects', label: 'Projects', icon: 'clipboard-check', color: '#6366f1' },
  { id: 'Procurement', label: 'Procure', icon: 'truck', color: BRAND.warning },
  { id: 'Production', label: 'Prod.', icon: 'factory', color: BRAND.danger },
];

const STATUS_COLORS = {
  active: BRAND.success, completed: BRAND.success, paid: BRAND.success, 'in-stock': BRAND.success,
  pending: BRAND.warning, 'in-progress': BRAND.info, draft: BRAND.sub,
  overdue: BRAND.danger, 'low-stock': BRAND.danger, leave: BRAND.warning,
  approved: BRAND.success, lead: BRAND.canary, proposal: BRAND.info, negotiation: BRAND.warning,
};

const ERPSearchScreen = ({ navigation }) => {
  const [query, setQuery] = useState('');
  const [activeModule, setActiveModule] = useState('all');
  const [recentSearches, setRecentSearches] = useState(RECENT_SEARCHES);
  const [showDetail, setShowDetail] = useState(false);
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 400, useNativeDriver: true }).start();
    setTimeout(() => inputRef.current?.focus(), 300);
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (showDetail) { setShowDetail(false); return true; }
      if (query) { setQuery(''); return true; }
      return false;
    });
    return () => sub.remove();
  }, [showDetail, query]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    let r = SEARCH_INDEX.filter(item =>
      String(item.title ?? '').toLowerCase().includes(q) ||
      String(item.sub ?? '').toLowerCase().includes(q) ||
      item.tags.some(t => t.toLowerCase().includes(q)) ||
      String(item.module ?? '').toLowerCase().includes(q) ||
      String(item.type ?? '').toLowerCase().includes(q) ||
      String(item.status ?? '').toLowerCase().includes(q)
    );
    if (activeModule !== 'all') r = r.filter(i => i.module === activeModule);
    return r;
  }, [query, activeModule]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach(r => { if (!g[r.module]) g[r.module] = []; g[r.module].push(r); });
    return g;
  }, [results]);

  const saveSearch = useCallback(async (q) => {
    if (!q.trim()) return;
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 8);
    setRecentSearches(updated);
    try { await AsyncStorage.setItem('erp_recent_searches', JSON.stringify(updated)); } catch {}
  }, [recentSearches]);

  const handleSearch = (q) => {
    setQuery(q);
    if (q.length > 2) saveSearch(q);
  };

  const openDetail = (item) => { setSelected(item); setShowDetail(true); };

  const clearRecent = () => {
    Alert.alert('Clear History', 'Remove all recent searches?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Clear', style: 'destructive', onPress: () => { setRecentSearches([]); AsyncStorage.removeItem('erp_recent_searches'); } },
    ]);
  };

  const renderResultCard = (item) => {
    const typeCfg = TYPE_ICONS[item.type] || { icon: 'information', color: BRAND.blue, label: item.type };
    const statusColor = STATUS_COLORS[item.status] || BRAND.sub;
    return (
      <TouchableOpacity key={item.id} style={[ss.resultCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]} onPress={() => openDetail(item)} activeOpacity={0.75}>
        <View style={[ss.resultIcon, { backgroundColor: item.color + '18' }]}>
          <MaterialCommunityIcons name={item.icon} size={20} color={item.color} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[ss.resultTitle, { color: BRAND.text }]} numberOfLines={1}>{item.title}</Text>
          <Text style={[ss.resultSub, { color: BRAND.sub }]} numberOfLines={1}>{item.sub}</Text>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: 4, alignItems: 'center' }}>
            <View style={[ss.typeBadge, { backgroundColor: typeCfg.color + '15' }]}>
              <Text style={[ss.typeBadgeText, { color: typeCfg.color }]}>{typeCfg.label}</Text>
            </View>
            <View style={[ss.typeBadge, { backgroundColor: statusColor + '15' }]}>
              <Text style={[ss.typeBadgeText, { color: statusColor }]}>{item.status}</Text>
            </View>
          </View>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
          <Text style={[ss.resultValue, { color: item.status === 'overdue' || item.status === 'low-stock' ? BRAND.danger : BRAND.text }]}>{item.value}</Text>
          <View style={[ss.modBadge, { backgroundColor: item.color + '10' }]}>
            <Text style={[ss.modBadgeText, { color: item.color }]}>{item.module}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const renderEmptyState = () => (
    <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
      {/* AI Suggestions */}
      <View style={{ paddingHorizontal: 16, marginBottom: 24 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: '#6366f1' + '18', justifyContent: 'center', alignItems: 'center' }}>
            <MaterialCommunityIcons name="robot" size={14} color="#6366f1" />
          </View>
          <Text style={[ss.sectionTitle, { color: BRAND.text }]}>AI Search Suggestions</Text>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {AI_SUGGESTIONS.map((s, i) => (
            <TouchableOpacity key={i} style={[ss.aiSuggestion, { backgroundColor: '#6366f1' + '10', borderColor: '#6366f1' + '30' }]} onPress={() => handleSearch(s)}>
              <MaterialCommunityIcons name="magnify" size={11} color="#6366f1" />
              <Text style={[ss.aiSuggestionText, { color: '#6366f1' }]}>{s}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Recent Searches */}
      {recentSearches.length > 0 && (
        <View style={{ paddingHorizontal: 16, marginBottom: 24 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Text style={[ss.sectionTitle, { color: BRAND.text }]}>Recent Searches</Text>
            <TouchableOpacity onPress={clearRecent}><Text style={{ color: BRAND.danger, fontSize: 12, fontWeight: '600' }}>Clear</Text></TouchableOpacity>
          </View>
          {recentSearches.map((s, i) => (
            <TouchableOpacity key={i} style={[ss.recentRow, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]} onPress={() => handleSearch(s)}>
              <MaterialCommunityIcons name="history" size={16} color={BRAND.sub} />
              <Text style={[ss.recentText, { color: BRAND.text, flex: 1 }]}>{s}</Text>
              <TouchableOpacity onPress={() => setRecentSearches(prev => prev.filter(r => r !== s))}>
                <MaterialCommunityIcons name="close" size={14} color={BRAND.line} />
              </TouchableOpacity>
            </TouchableOpacity>
          ))}
        </View>
      )}

      {/* Browse by module */}
      <View style={{ paddingHorizontal: 16 }}>
        <Text style={[ss.sectionTitle, { color: BRAND.text, marginBottom: 12 }]}>Browse by Module</Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {MODULE_FILTERS.filter(m => m.id !== 'all').map((mod, i) => (
            <TouchableOpacity key={i} style={[ss.modBrowseCard, { backgroundColor: BRAND.surface, borderColor: mod.color + '30', borderWidth: 1.5 }]} onPress={() => { setActiveModule(mod.id); handleSearch(mod.label); }}>
              <View style={[ss.modBrowseIcon, { backgroundColor: mod.color + '18' }]}>
                <MaterialCommunityIcons name={mod.icon} size={22} color={mod.color} />
              </View>
              <Text style={[ss.modBrowseName, { color: BRAND.text }]}>{mod.label}</Text>
              <Text style={[ss.modBrowseCount, { color: BRAND.sub }]}>{SEARCH_INDEX.filter(x => x.module === mod.id).length} records</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Animated.View>
  );

  const renderResults = () => (
    <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 80 }}>
      {/* Results count */}
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Text style={[ss.sectionTitle, { color: BRAND.text }]}>{results.length} result{results.length !== 1 ? 's' : ''} for "{query}"</Text>
        {results.length > 0 && <Text style={{ color: BRAND.sub, fontSize: 11 }}>{activeModule !== 'all' ? activeModule : 'All modules'}</Text>}
      </View>

      {results.length === 0 ? (
        <View style={ss.noResults}>
          <MaterialCommunityIcons name="magnify-close" size={56} color={BRAND.line} />
          <Text style={[ss.noResultsTitle, { color: BRAND.text }]}>No results found</Text>
          <Text style={[ss.noResultsSub, { color: BRAND.sub }]}>Try different keywords or remove filters</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 16 }}>
            {AI_SUGGESTIONS.slice(0, 4).map((s, i) => (
              <TouchableOpacity key={i} style={[ss.aiSuggestion, { backgroundColor: BRAND.blue + '10', borderColor: BRAND.blue + '30' }]} onPress={() => handleSearch(s)}>
                <Text style={[ss.aiSuggestionText, { color: BRAND.blue }]}>{s}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      ) : activeModule !== 'all' ? (
        results.map(r => renderResultCard(r))
      ) : (
        Object.entries(grouped).map(([module, items]) => {
          const modCfg = MODULE_FILTERS.find(m => m.id === module) || { color: BRAND.blue, icon: 'apps' };
          return (
            <View key={module} style={{ marginBottom: 20 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <View style={[ss.groupIcon, { backgroundColor: modCfg.color + '18' }]}>
                  <MaterialCommunityIcons name={modCfg.icon} size={14} color={modCfg.color} />
                </View>
                <Text style={[ss.groupTitle, { color: modCfg.color }]}>{module}</Text>
                <View style={[ss.groupCount, { backgroundColor: modCfg.color + '15' }]}>
                  <Text style={[ss.groupCountText, { color: modCfg.color }]}>{items.length}</Text>
                </View>
              </View>
              {items.map(r => renderResultCard(r))}
            </View>
          );
        })
      )}
    </ScrollView>
  );

  return (
    <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
      <StatusBar barStyle="light-content" backgroundColor={BRAND.blue} />
      <LinearGradient colors={[BRAND.blue, '#163b6c']} style={ss.header}>
        <View style={ss.headerRow}>
          <TouchableOpacity style={ss.backBtn} onPress={() => navigation?.goBack()}><MaterialCommunityIcons name="arrow-left" size={22} color="#fff" /></TouchableOpacity>
          <View style={[ss.searchInputWrapper, { backgroundColor: 'rgba(255,255,255,0.18)' }]}>
            <MaterialCommunityIcons name="magnify" size={18} color="rgba(255,255,255,0.8)" />
            <TextInput
              ref={inputRef}
              value={query}
              onChangeText={handleSearch}
              placeholder="Search everything in ERP…"
              placeholderTextColor="rgba(255,255,255,0.6)"
              style={[ss.searchMainInput, { color: '#fff' }]}
              returnKeyType="search"
              autoCapitalize="none"
              autoCorrect={false}
            />
            {query ? (
              <TouchableOpacity onPress={() => setQuery('')}>
                <MaterialCommunityIcons name="close-circle" size={16} color="rgba(255,255,255,0.7)" />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
        {/* Module filter pills */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ paddingBottom: 10 }}>
          {MODULE_FILTERS.map(mod => (
            <TouchableOpacity key={mod.id} style={[ss.modFilterPill, activeModule === mod.id && { backgroundColor: '#fff' }, { borderColor: activeModule === mod.id ? '#fff' : 'rgba(255,255,255,0.3)' }]} onPress={() => setActiveModule(mod.id)}>
              <MaterialCommunityIcons name={mod.icon} size={11} color={activeModule === mod.id ? mod.color : 'rgba(255,255,255,0.8)'} />
              <Text style={[ss.modFilterText, { color: activeModule === mod.id ? mod.color : 'rgba(255,255,255,0.85)' }]}>{mod.label}</Text>
            </TouchableOpacity>
          ))}
          <View style={{ width: 16 }} />
        </ScrollView>
      </LinearGradient>

      <View style={{ flex: 1 }}>
        {query.trim() ? renderResults() : (
          <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingTop: 20, paddingBottom: 80 }}>
            {renderEmptyState()}
          </ScrollView>
        )}
      </View>

      {/* DETAIL MODAL */}
      <Modal visible={showDetail} animationType="slide" presentationStyle="formSheet" onRequestClose={() => setShowDetail(false)}>
        {selected && (() => {
          const typeCfg = TYPE_ICONS[selected.type] || { icon: 'information', color: BRAND.blue, label: selected.type };
          const statusColor = STATUS_COLORS[selected.status] || BRAND.sub;
          return (
            <View style={{ flex: 1, backgroundColor: BRAND.bg }}>
              <LinearGradient colors={[selected.color, selected.color + 'aa']} style={{ flexDirection: 'row', alignItems: 'center', padding: 20, gap: 12 }}>
                <TouchableOpacity style={ss.modalClose} onPress={() => setShowDetail(false)}><MaterialCommunityIcons name="close" size={22} color="#fff" /></TouchableOpacity>
                <View style={[ss.detailTypeIcon, { backgroundColor: 'rgba(255,255,255,0.2)' }]}>
                  <MaterialCommunityIcons name={selected.icon} size={22} color="#fff" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ color: '#fff', fontSize: 15, fontWeight: '800' }} numberOfLines={2}>{selected.title}</Text>
                  <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, marginTop: 2 }}>{typeCfg.label} · {selected.module}</Text>
                </View>
              </LinearGradient>
              <ScrollView style={{ flex: 1, padding: 20 }}>
                <View style={[ss.detailCard, { backgroundColor: BRAND.surface, borderColor: BRAND.line }]}>
                  {[
                    { l: 'Record ID', v: selected.id },
                    { l: 'Module', v: selected.module },
                    { l: 'Type', v: typeCfg.label },
                    { l: 'Details', v: selected.sub },
                    { l: 'Value', v: selected.value },
                    { l: 'Status', v: selected.status },
                  ].map((f, i) => (
                    <View key={i} style={[ss.detailRow, { borderBottomColor: BRAND.line, borderBottomWidth: i < 5 ? 1 : 0 }]}>
                      <Text style={[ss.detailLabel, { color: BRAND.sub }]}>{f.l}</Text>
                      <Text style={[ss.detailValue, { color: f.l === 'Status' ? statusColor : BRAND.text }]} numberOfLines={2}>{f.v}</Text>
                    </View>
                  ))}
                </View>
                {selected.tags?.length > 0 && (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                    {selected.tags.map((t, ti) => (
                      <TouchableOpacity key={ti} style={[ss.detailTag, { backgroundColor: selected.color + '12' }]} onPress={() => { setShowDetail(false); handleSearch(t); }}>
                        <Text style={[ss.detailTagText, { color: selected.color }]}>#{t}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
                <View style={ss.detailActions}>
                  {[
                    { l: 'Open in Module', i: 'arrow-right-circle', c: selected.color, onPress: () => { setShowDetail(false); navigation?.navigate?.('ERPModuleScreen', { module: { id: String(selected.module ?? '').toLowerCase(), name: selected.module, color: selected.color, icon: selected.icon } }); } },
                    { l: 'Search Similar', i: 'magnify', c: BRAND.blue, onPress: () => { setShowDetail(false); handleSearch(selected.type); } },
                  ].map((a, i) => (
                    <TouchableOpacity key={i} style={[ss.detailActionBtn, { borderColor: a.c + '40', backgroundColor: a.c + '10', flex: 1 }]} onPress={a.onPress}>
                      <MaterialCommunityIcons name={a.i} size={16} color={a.c} />
                      <Text style={[ss.detailActionText, { color: a.c }]}>{a.l}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>
          );
        })()}
      </Modal>
    </View>
  );
};

const ss = StyleSheet.create({
  header: { paddingTop: Platform.OS === 'android' ? (StatusBar.currentHeight || 24) : 0, paddingHorizontal: 16, paddingBottom: 0 },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12 },
  backBtn: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  searchInputWrapper: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 10, borderRadius: 14 },
  searchMainInput: { flex: 1, fontSize: 15, fontWeight: '500' },
  modFilterPill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, borderWidth: 1, marginRight: 8, marginBottom: 6 },
  modFilterText: { fontSize: 11, fontWeight: '600' },
  sectionTitle: { fontSize: 15, fontWeight: '700' },
  aiSuggestion: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 7, borderRadius: 12, borderWidth: 1 },
  aiSuggestionText: { fontSize: 12, fontWeight: '600' },
  recentRow: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 13, borderRadius: 12, borderWidth: 1, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 3 },
  recentText: { fontSize: 13 },
  modBrowseCard: { width: (W - 52) / 2, borderRadius: 16, padding: 14, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  modBrowseIcon: { width: 44, height: 44, borderRadius: 13, justifyContent: 'center', alignItems: 'center', marginBottom: 10 },
  modBrowseName: { fontSize: 14, fontWeight: '700', marginBottom: 3 },
  modBrowseCount: { fontSize: 11 },
  resultCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 13, borderRadius: 14, borderWidth: 1, marginBottom: 8, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  resultIcon: { width: 44, height: 44, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  resultTitle: { fontSize: 13, fontWeight: '700', marginBottom: 2 },
  resultSub: { fontSize: 11 },
  resultValue: { fontSize: 13, fontWeight: '700' },
  typeBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 7 },
  typeBadgeText: { fontSize: 9, fontWeight: '700' },
  modBadge: { paddingHorizontal: 7, paddingVertical: 2, borderRadius: 7 },
  modBadgeText: { fontSize: 9, fontWeight: '700' },
  groupIcon: { width: 26, height: 26, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  groupTitle: { fontSize: 13, fontWeight: '800' },
  groupCount: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  groupCountText: { fontSize: 11, fontWeight: '700' },
  noResults: { alignItems: 'center', paddingVertical: 60 },
  noResultsTitle: { fontSize: 18, fontWeight: '700', marginTop: 14, marginBottom: 6 },
  noResultsSub: { fontSize: 13, textAlign: 'center' },
  modalClose: { width: 34, height: 34, borderRadius: 17, backgroundColor: 'rgba(255,255,255,0.2)', justifyContent: 'center', alignItems: 'center' },
  detailTypeIcon: { width: 42, height: 42, borderRadius: 13, justifyContent: 'center', alignItems: 'center' },
  detailCard: { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginBottom: 14, elevation: 1, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4 },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', padding: 13 },
  detailLabel: { fontSize: 12, width: 90 },
  detailValue: { fontSize: 13, fontWeight: '600', flex: 1, textAlign: 'right' },
  detailTag: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10 },
  detailTagText: { fontSize: 12, fontWeight: '600' },
  detailActions: { flexDirection: 'row', gap: 10, marginBottom: 30 },
  detailActionBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 14, borderRadius: 14, borderWidth: 1 },
  detailActionText: { fontSize: 13, fontWeight: '700' },
});

export default ERPSearchScreen;